import { useTranslation } from 'react-i18next';
import { FixQuality } from '@fla/core';
import { useStore } from '../store';
import { IconSatellite } from './icons';

function qualityKey(q?: number): string {
  if (q === FixQuality.RTKFixed) return 'fixed';
  if (q === FixQuality.RTKFloat) return 'float';
  if (q === FixQuality.DGPS) return 'dgps';
  if (q === FixQuality.GPS) return 'gps';
  if (q === FixQuality.Simulation) return 'sim';
  return 'noFix';
}

function qualityColor(q?: number): string {
  if (q === FixQuality.RTKFixed || q === FixQuality.Simulation) return 'bg-emerald-600';
  if (q === FixQuality.RTKFloat || q === FixQuality.GPS) return 'bg-amber-500';
  return 'bg-zinc-600';
}

export function StatusBar() {
  const { t } = useTranslation();
  const fix = useStore((s) => s.fix);
  const connected = useStore((s) => s.connected);
  const q = fix?.quality;

  return (
    // Cap the row so the pills never slide under the map/burger buttons on a narrow phone; tighter
    // sizing on phone, roomier from md: up (tablet / in-cab screen).
    <div className="top-safe pointer-events-none absolute left-3 flex max-w-[calc(100vw-7rem)] items-center gap-1.5 md:max-w-none md:gap-2">
      <div
        className={`flex h-8 shrink-0 items-center gap-1.5 rounded-full ${qualityColor(q)} px-2.5 text-xs font-semibold text-white shadow-lg md:h-9 md:gap-2 md:px-3 md:text-sm`}
      >
        <span className={`h-2 w-2 rounded-full ${connected ? 'bg-white' : 'bg-white/40'}`} />
        {fix ? t(`gps.${qualityKey(q)}`) : t('gps.searching')}
      </div>
      {fix && (
        <div className="flex h-8 min-w-0 items-center gap-1.5 rounded-full bg-black/60 px-2.5 text-xs text-white/90 backdrop-blur md:h-9 md:px-3 md:text-sm">
          <span className="tabular-nums">
            {t('gps.speed', { kmh: ((fix.speed ?? 0) * 3.6).toFixed(1) })}
          </span>
          <span className="text-white/40">·</span>
          {fix.accuracyM != null ? (
            <span className="tabular-nums">
              {t('gps.accuracy', { m: Math.round(fix.accuracyM) })}
            </span>
          ) : (
            <span
              className="flex items-center gap-1 tabular-nums"
              title={t('gps.satsTitle', { n: fix.satellites })}
            >
              <IconSatellite className="h-3.5 w-3.5 md:h-4 md:w-4" />
              {fix.satellites}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
